import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { CalculatorForm } from "./CalculatorForm";
import { CalculationResults } from "./CalculationResults";
import { Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { 
  FormData, 
  CalculationResult, 
  calculateSavingsResult, 
  saveCalculationResults 
} from "@/utils/calculationUtils";

export const CalculatorCard = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    salary: "",
    obligations: "",
  });
  const [result, setResult] = useState<CalculationResult | null>(null);

  const checkUrgentSubmission = () => {
    const startTime = localStorage.getItem('documentSubmissionStartTime');
    if (!startTime) return false;
    
    const now = new Date().getTime();
    const timePassed = now - parseInt(startTime);
    const timeRemaining = 24 * 60 * 60 * 1000 - timePassed;
    
    return timeRemaining > 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateSavings = async () => {
    if (!formData.name || !formData.email || !formData.salary || !formData.obligations) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields to calculate your savings.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to save your calculations.",
        variant: "destructive",
      });
      return;
    }

    const obligations = parseFloat(formData.obligations);
    const isUrgent = checkUrgentSubmission();
    const calculationResult = calculateSavingsResult(obligations, isUrgent);

    try {
      await saveCalculationResults(formData, calculationResult, user.id);
      setResult(calculationResult);
      
      toast({
        title: "Calculation Complete",
        description: "We've calculated and saved your potential savings!",
      });
    } catch (error) {
      console.error('Error saving calculation:', error);
      toast({
        title: "Error",
        description: "Failed to save calculation results. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="bg-black/80 backdrop-blur-lg border border-white/10 p-6 space-y-6 max-w-md w-full mx-auto animate-in">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight text-white">Calculate Your Savings</h2>
        <p className="text-sm text-gray-300">
          Find out how much you could save with our financial realignment services.
        </p>
      </div>

      <CalculatorForm 
        formData={formData}
        onInputChange={handleInputChange}
        onCalculate={calculateSavings}
      />

      {result && <CalculationResults result={result} />}

      <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-white">Step 3: Fast-Track Financial Freedom</h3>
        </div>
        <p className="text-sm text-gray-300">
          Within just one week, our expert financial legal team can restructure your finances, 
          significantly reducing your monthly payments. Act now to unlock additional savings!
        </p>
        <div className="mt-3 text-sm text-primary font-medium">
          Limited Time Offer: Submit your documents today for maximum benefits!
        </div>
      </div>
    </Card>
  );
};